import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Trash2,
  Edit,
  ArrowLeft,
  Shield,
  UserCog,
  ClipboardCheck,
  X,
  Save,
  Camera,
  Phone,
  Mail,
  Eye,
  FileText,
  Calendar,
  Building2,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders, isMaster, toggleRole, getEffectiveRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'reports'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(null); // User to edit roles
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'field_qc_engineer',
    roles: ['field_qc_engineer'],
    phone: '',
    photo: ''
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isMaster()) {
      navigate('/');
      return;
    }
    fetchUsers();
    fetchReports();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/reports`, {
        headers: getAuthHeaders()
      });
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/users`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/admin/users/${editingUser.id}`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('User updated successfully');
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleDeleteReport = async (reportId, reportName) => {
    if (!window.confirm(`Are you sure you want to delete report "${reportName}"? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/reports/${reportId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Report deleted successfully');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete report');
    }
  };

  const handleToggleRole = async () => {
    try {
      const newRole = await toggleRole();
      toast.success(`Switched to ${newRole === 'field_qc_engineer' ? 'QC Engineer' : 'Review Engineer'} mode`);
    } catch (error) {
      toast.error('Failed to toggle role');
    }
  };

  const handleUpdateUserRoles = async (userId, newRoles) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/roles`, {
        user_id: userId,
        roles: newRoles
      }, {
        headers: getAuthHeaders()
      });
      toast.success('User roles updated successfully');
      fetchUsers();
      setShowRoleModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update roles');
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', role: 'field_qc_engineer', roles: ['field_qc_engineer'], phone: '', photo: '' });
  };

  const startEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      email: userToEdit.email,
      password: '',
      name: userToEdit.name,
      role: userToEdit.role,
      roles: userToEdit.roles || [userToEdit.role],
      phone: userToEdit.phone || '',
      photo: userToEdit.photo || ''
    });
  };

  const getRoleIcon = (role, isDualRole = false) => {
    if (isDualRole) {
      return <Users className="w-4 h-4 text-cyan-500" />;
    }
    switch (role) {
      case 'master':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'review_engineer':
        return <ClipboardCheck className="w-4 h-4 text-blue-500" />;
      default:
        return <UserCog className="w-4 h-4 text-green-500" />;
    }
  };

  const getRoleBadge = (role, isMasterAccount, userRoles = null) => {
    if (isMasterAccount) {
      return <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-700">Master</span>;
    }
    // Check for dual roles
    const isDualRole = userRoles && userRoles.length > 1;
    if (isDualRole) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded bg-cyan-100 text-cyan-700 flex items-center gap-1">
          <Users className="w-3 h-3" />
          QC + Review
        </span>
      );
    }
    switch (role) {
      case 'review_engineer':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">Review Engineer</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">QC Engineer</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case 'submitted':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700 flex items-center gap-1"><Clock className="w-3 h-3" /> Submitted</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded bg-slate-100 text-slate-700 flex items-center gap-1"><FileText className="w-3 h-3" /> Draft</span>;
    }
  };

  const UserAvatar = ({ user: u, size = 'md' }) => {
    const sizes = {
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-base',
      lg: 'w-20 h-20 text-2xl'
    };
    
    if (u.photo) {
      return (
        <img 
          src={u.photo} 
          alt={u.name} 
          className={`${sizes[size]} rounded-full object-cover border-2 border-slate-200`}
        />
      );
    }
    
    return (
      <div className={`${sizes[size]} rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold`}>
        {u.name?.charAt(0).toUpperCase() || '?'}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-purple-600" />
                <h1 className="text-xl font-bold text-slate-900">Admin Panel</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                Active Mode: <span className="font-semibold text-slate-900">
                  {getEffectiveRole() === 'field_qc_engineer' ? 'QC Engineer' : 'Review Engineer'}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={handleToggleRole}>
                Toggle Role
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Master</p>
                <p className="text-xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'master').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCog className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">QC Engineers</p>
                <p className="text-xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'field_qc_engineer').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Reviewers</p>
                <p className="text-xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'review_engineer').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <FileText className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Reports</p>
                <p className="text-xl font-bold text-slate-900">{reports.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-slate-900' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            User Management
          </Button>
          <Button
            variant={activeTab === 'reports' ? 'default' : 'outline'}
            onClick={() => setActiveTab('reports')}
            className={activeTab === 'reports' ? 'bg-slate-900' : ''}
          >
            <FileText className="w-4 h-4 mr-2" />
            Reports ({reports.length})
          </Button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-600" />
                <h2 className="text-lg font-semibold text-slate-900">User Management</h2>
              </div>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-1" /> Create User
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">User</th>
                    <th className="text-left p-4 font-medium text-slate-600">Contact</th>
                    <th className="text-left p-4 font-medium text-slate-600">Role</th>
                    <th className="text-left p-4 font-medium text-slate-600">Created</th>
                    <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <UserAvatar user={u} size="md" />
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        {u.phone ? (
                          <span className="flex items-center gap-1 text-slate-600">
                            <Phone className="w-3 h-3" /> {u.phone}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">Not provided</span>
                        )}
                      </td>
                      <td className="p-4">{getRoleBadge(u.role, u.is_master, u.roles)}</td>
                      <td className="p-4 text-slate-600">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setViewingUser(u)} title="View Profile">
                            <Eye className="w-4 h-4 text-slate-500" />
                          </Button>
                          {!u.is_master && u.role !== 'master' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => setShowRoleModal(u)}
                                title="Edit Roles"
                                className="text-cyan-600 hover:text-cyan-700"
                              >
                                <Users className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => startEdit(u)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteUser(u.id, u.name)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                          {(u.is_master || u.role === 'master') && (
                            <span className="text-xs text-slate-400">Protected</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                <h2 className="text-lg font-semibold text-slate-900">All Reports ({reports.length})</h2>
              </div>
              <div className="flex gap-2 text-sm">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                  Approved: {reports.filter(r => r.status === 'approved').length}
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  Submitted: {reports.filter(r => r.status === 'submitted').length}
                </span>
                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">
                  Draft: {reports.filter(r => r.status === 'draft').length}
                </span>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Report ID</th>
                    <th className="text-left p-4 font-medium text-slate-600">Client / Project</th>
                    <th className="text-left p-4 font-medium text-slate-600">Created By</th>
                    <th className="text-left p-4 font-medium text-slate-600">Status</th>
                    <th className="text-left p-4 font-medium text-slate-600">Date</th>
                    <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {reports.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-slate-500">
                        No reports found
                      </td>
                    </tr>
                  ) : (
                    reports.map((report) => (
                      <tr key={report.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <span className="font-mono text-sm font-medium text-slate-900">
                            {report.report_id}
                          </span>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-slate-900">{report.client_name}</p>
                            <p className="text-sm text-slate-500">{report.project_name}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-600">{report.created_by_name}</span>
                        </td>
                        <td className="p-4">{getStatusBadge(report.status)}</td>
                        <td className="p-4">
                          <div className="text-sm">
                            <p className="text-slate-600">
                              {report.date_of_inspection ? new Date(report.date_of_inspection).toLocaleDateString() : '-'}
                            </p>
                            <p className="text-xs text-slate-400">Inspection</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => navigate(`/inspection/${report.id}`)}
                              title="View Report"
                            >
                              <Eye className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteReport(report.id, report.report_id)}
                              title="Delete Report"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Create/Edit User Modal */}
      {(showCreateModal || editingUser) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingUser(null);
                  resetForm();
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4">
              {/* Photo Upload */}
              <div className="flex flex-col items-center">
                <div 
                  className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-dashed border-slate-300 cursor-pointer hover:border-cyan-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {formData.photo ? (
                    <img src={formData.photo} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50 text-slate-400">
                      <Camera className="w-6 h-6 mb-1" />
                      <span className="text-xs">Add Photo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                {formData.photo && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setFormData({ ...formData, photo: '' })}
                    className="mt-2 text-red-500"
                  >
                    Remove Photo
                  </Button>
                )}
              </div>

              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  required={!editingUser}
                />
              </div>
              <div>
                <Label>Role</Label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                >
                  <option value="field_qc_engineer">QC Engineer</option>
                  <option value="review_engineer">Review Engineer</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  <Save className="w-4 h-4 mr-1" />
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View User Profile Modal */}
      {viewingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">User Profile</h3>
              <Button variant="ghost" size="sm" onClick={() => setViewingUser(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6">
              <div className="flex flex-col items-center mb-6">
                <UserAvatar user={viewingUser} size="lg" />
                <h4 className="mt-3 text-xl font-semibold text-slate-900">{viewingUser.name}</h4>
                <div className="mt-1">{getRoleBadge(viewingUser.role, viewingUser.is_master)}</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="text-slate-900">{viewingUser.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="text-slate-900">{viewingUser.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Roles Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-md w-full">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold">Edit User Roles</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRoleModal(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <UserAvatar user={showRoleModal} size="md" />
                <div>
                  <p className="font-medium text-slate-900">{showRoleModal.name}</p>
                  <p className="text-sm text-slate-500">{showRoleModal.email}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Assign Roles</Label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border rounded-sm cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={(showRoleModal.roles || [showRoleModal.role]).includes('field_qc_engineer')}
                      onChange={(e) => {
                        const currentRoles = showRoleModal.roles || [showRoleModal.role];
                        let newRoles;
                        if (e.target.checked) {
                          newRoles = [...new Set([...currentRoles, 'field_qc_engineer'])];
                        } else {
                          newRoles = currentRoles.filter(r => r !== 'field_qc_engineer');
                          if (newRoles.length === 0) newRoles = ['review_engineer'];
                        }
                        setShowRoleModal({ ...showRoleModal, roles: newRoles });
                      }}
                      className="w-4 h-4 text-cyan-600 rounded"
                    />
                    <div className="flex items-center gap-2">
                      <UserCog className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-slate-900">QC Engineer</p>
                        <p className="text-xs text-slate-500">Can create and edit inspections</p>
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border rounded-sm cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={(showRoleModal.roles || [showRoleModal.role]).includes('review_engineer')}
                      onChange={(e) => {
                        const currentRoles = showRoleModal.roles || [showRoleModal.role];
                        let newRoles;
                        if (e.target.checked) {
                          newRoles = [...new Set([...currentRoles, 'review_engineer'])];
                        } else {
                          newRoles = currentRoles.filter(r => r !== 'review_engineer');
                          if (newRoles.length === 0) newRoles = ['field_qc_engineer'];
                        }
                        setShowRoleModal({ ...showRoleModal, roles: newRoles });
                      }}
                      className="w-4 h-4 text-cyan-600 rounded"
                    />
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-slate-900">Review Engineer</p>
                        <p className="text-xs text-slate-500">Can review and approve inspections</p>
                      </div>
                    </div>
                  </label>
                </div>
                
                {(showRoleModal.roles || []).length > 1 && (
                  <div className="mt-3 p-3 bg-cyan-50 border border-cyan-200 rounded-sm">
                    <p className="text-sm text-cyan-700 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span><strong>Dual Role:</strong> User can switch between QC and Review modes</span>
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => handleUpdateUserRoles(showRoleModal.id, showRoleModal.roles || [showRoleModal.role])}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Roles
                </Button>
                <Button variant="outline" onClick={() => setShowRoleModal(null)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
